import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AtPoint, useRadarView } from "./Radar.tsx";

/** Markers closer than this on screen, in CSS pixels, stack into one pill. */
export const STACK_DISTANCE = 26;

/**
 * Throw spots this close (world units, under a third of a step) are the same
 * spot: one getpos pasted twice, give or take. Farther apart, they stay separate
 * dots even when they nearly touch on the map.
 */
export const SAME_SPOT_UNITS = 8;

/** Where an item is thrown from: on the radar, and in the world when known (to tell the same spot from a close one). */
export interface Origin {
  u: number;
  v: number;
  x?: number;
  y?: number;
  z?: number;
}

/** True when two throw spots are the same spot (see SAME_SPOT_UNITS). */
export function sameSpot(a: Origin, b: Origin): boolean {
  if (a.x != null && a.y != null && b.x != null && b.y != null)
    return Math.hypot(a.x - b.x, a.y - b.y) <= SAME_SPOT_UNITS && Math.abs((a.z ?? 0) - (b.z ?? 0)) < 32;
  return Math.hypot(a.u - b.u, a.v - b.v) < 0.0015;
}

export interface MapItem {
  key: string;
  u: number;
  v: number;
}

export interface Cluster<T extends MapItem> {
  key: string;
  u: number;
  v: number;
  items: T[];
}

/**
 * Groups points that would overlap at this zoom: a point joins the first group
 * whose centre is within `distance` (radar fractions), and the centre follows.
 */
export function cluster<T extends MapItem>(items: T[], distance: number): Cluster<T>[] {
  const groups: { u: number; v: number; items: T[] }[] = [];
  for (const item of items) {
    const g = groups.find((c) => Math.hypot(c.u - item.u, c.v - item.v) < distance);
    if (g) {
      g.items.push(item);
      g.u += (item.u - g.u) / g.items.length;
      g.v += (item.v - g.v) / g.items.length;
    } else groups.push({ u: item.u, v: item.v, items: [item] });
  }
  return groups.map((g) => ({ ...g, key: g.items.map((i) => i.key).sort().join("|") }));
}

interface Props<T extends MapItem> {
  items: T[];
  /** One marker on its own. */
  renderMarker: (item: T) => ReactNode;
  /** Its icon in the stack's list and beside a throw spot (a grenade icon, a combo's glyphs). */
  renderGlyph: (item: T) => ReactNode;
  /** Its drawing in the stack's pill, where room is short (else `renderGlyph`). */
  renderPillGlyph?: (item: T) => ReactNode;
  /** What kind it is: the pill shows each kind once, the most common first (three smokes and a molotov: a smoke, a molotov, 4). */
  kindOf?: (item: T) => string;
  /** Its side: a stack of one side is ringed in that side's colour, like a single marker. */
  sideOf?: (item: T) => "t" | "ct";
  /** Its line in the stack's list. */
  label: (item: T) => string;
  detail?: (item: T) => string;
  isSelected: (item: T) => boolean;
  onSelect: (item: T) => void;
  /** Pointed at from elsewhere (a card in a list): its stack lights up. */
  isHot?: (item: T) => boolean;
  /**
   * The other end of an item (where a landing marker is thrown from), on this
   * floor; null when it is elsewhere. An open stack draws a thin line to each.
   */
  origin?: (item: T) => Origin | null;
  /** What sits at a throw spot (the grenade's launch icon); a plain dot without it. */
  renderOrigin?: (item: T) => ReactNode;
  /** A class for its line to the throw spot ("side-t" colours it). */
  lineClass?: (item: T) => string | undefined;
}

type ListProps<T extends MapItem> = Pick<Props<T>, "renderGlyph" | "label" | "detail" | "isSelected" | "onSelect">;

/**
 * A stack on the map: up to three drawings (each kind once, the most common
 * first) and how many there are, ringed in the side's colour when they all
 * share one, on a few cards' worth of shadow.
 */
function StackPill<T extends MapItem>({
  items,
  renderGlyph,
  kindOf,
  sideOf,
  marks,
}: {
  items: T[];
  renderGlyph: (item: T) => ReactNode;
  kindOf?: (item: T) => string;
  sideOf?: (item: T) => "t" | "ct";
  marks: boolean;
}) {
  let shown = items;
  if (kindOf) {
    const counts = new Map<string, { item: T; n: number; first: number }>();
    items.forEach((item, i) => {
      const k = kindOf(item);
      const c = counts.get(k);
      if (c) c.n += 1;
      else counts.set(k, { item, n: 1, first: i });
    });
    shown = [...counts.values()].sort((a, b) => b.n - a.n || a.first - b.first).map((c) => c.item);
  }
  const sides = new Set(sideOf ? items.map(sideOf) : []);
  const side = sides.size === 1 ? [...sides][0] : null;
  return (
    <span className={`stack-pill${marks ? " has-marks" : ""}${side ? ` side-${side}` : sideOf ? " is-mixed" : ""}`} aria-hidden="true">
      {shown.slice(0, 3).map((item) => (
        <span key={item.key} className="stack-glyph">
          {renderGlyph(item)}
        </span>
      ))}
      <span className="stack-count">{items.length}</span>
    </span>
  );
}

/** Room a name needs beside its dot, in pixels: closer to an edge, it goes on the other side. */
const LABEL_ROOM = 220;
const LABEL_EDGE = 28;

/** Which way a dot's name opens so the frame does not cut it: left near the right edge, down near the top, up near the bottom. */
function labelSide(view: ReturnType<typeof useRadarView>, u: number, v: number): string {
  if (!view.width) return "";
  const p = view.toScreen(u, v);
  // The radar is square.
  const height = view.width;
  return `${p.x > view.width - LABEL_ROOM ? " is-left" : ""}${p.y < LABEL_EDGE ? " is-below" : p.y > height - LABEL_EDGE ? " is-above" : ""}`;
}

/**
 * Markers that stack when they would overlap. Opening a stack draws a thin line
 * from it to where each one is thrown from, with a dot there to pick it; only
 * stuffs thrown from the very same spot share a dot, which opens their list.
 * Without throw spots to show (another floor), the stack opens a list instead.
 */
export function StackedMarkers<T extends MapItem>({
  items,
  renderMarker,
  renderGlyph,
  renderPillGlyph,
  kindOf,
  sideOf,
  label,
  detail,
  isSelected,
  onSelect,
  isHot,
  origin,
  renderOrigin,
  lineClass,
}: Props<T>) {
  const { t } = useTranslation();
  const view = useRadarView();
  // The stack opened, as it was when clicked: zooming afterwards would split it
  // into other stacks, and its throw spots would vanish.
  const [open, setOpen] = useState<{ key: string; u: number; v: number } | null>(null);
  // A throw spot of the open stack, shared by several: its list replaces the stack's.
  const [sub, setSub] = useState<string | null>(null);
  const [hot, setHot] = useState<string | null>(null);
  // Before the frame is measured, assume a mid-size map.
  const distance = STACK_DISTANCE / ((view.width || 640) * view.scale);
  const opened = useMemo<Cluster<T> | null>(() => {
    if (!open) return null;
    const keys = new Set(open.key.split("|"));
    const kept = items.filter((i) => keys.has(i.key));
    return kept.length > 1 ? { ...open, items: kept } : null;
  }, [open, items]);
  // Everything else stacks for the current zoom; the opened stack stays whole.
  const groups = useMemo(() => {
    if (!opened) return cluster(items, distance);
    const inside = new Set(opened.items.map((i) => i.key));
    return [...cluster(items.filter((i) => !inside.has(i.key)), distance), opened];
  }, [items, distance, opened]);

  // Where the open stack's items are thrown from: one dot per spot, shared only by the very same spot.
  const ends = useMemo(() => {
    if (!opened || !origin) return [];
    const groups: { key: string; u: number; v: number; at: Origin; items: (T & { item: T })[] }[] = [];
    for (const item of opened.items) {
      const o = origin(item);
      if (!o) continue;
      const g = groups.find((x) => sameSpot(x.at, o));
      const entry = { ...item, item };
      if (g) g.items.push(entry);
      else groups.push({ key: "", u: o.u, v: o.v, at: o, items: [entry] });
    }
    return groups.map((g) => ({ ...g, key: g.items.map((i) => i.key).sort().join("|") }));
  }, [opened, origin]);
  // Every item has its dot: no list needed for the stack itself.
  const byDots = !!opened && ends.reduce((n, g) => n + g.items.length, 0) === opened.items.length;
  const subGroup = ends.find((g) => g.key === sub && g.items.length > 1) ?? null;

  // Lines open without a list: a click elsewhere or Escape closes them.
  useEffect(() => {
    if (!opened || !byDots || subGroup) return;
    // A click, not a press: dragging the map to look around keeps it open (the radar swallows
    // the click that ends a drag), and so do the zoom buttons.
    const away = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(".stack-marker, .stack-origin, .radar-zoom")) {
        setOpen(null);
        setHot(null);
      }
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && (setOpen(null), setHot(null));
    document.addEventListener("click", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("click", away);
      document.removeEventListener("keydown", esc);
    };
  }, [opened, byDots, subGroup]);

  const close = () => {
    setOpen(null);
    setSub(null);
    setHot(null);
  };
  const pick = (item: T) => {
    onSelect(item);
    close();
  };

  return (
    <>
      {opened && ends.length > 0 && (
        <svg className="stack-lines" viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden="true">
          {ends.flatMap((g) =>
            g.items.map((e) => (
              <line
                key={e.key}
                className={[lineClass?.(e.item), hot === e.key || isSelected(e.item) ? "is-hot" : ""].filter(Boolean).join(" ") || undefined}
                x1={opened.u * 1000}
                y1={opened.v * 1000}
                x2={g.u * 1000}
                y2={g.v * 1000}
              />
            )),
          )}
        </svg>
      )}
      {ends.map((g) =>
        g.items.length === 1 ? (
          <AtPoint key={`o${g.key}`} u={g.u} v={g.v} className={`stack-origin${hot === g.items[0].key ? " is-hot" : ""}`}>
            <button
              type="button"
              className={`origin-dot${renderOrigin ? " has-icon" : ""}${hot === g.items[0].key ? " is-hot" : ""}`}
              aria-label={t("radar.thrownFromHere", { names: label(g.items[0].item) })}
              onPointerEnter={() => setHot(g.items[0].key)}
              onPointerLeave={() => setHot(null)}
              onFocus={() => setHot(g.items[0].key)}
              onBlur={() => setHot(null)}
              onClick={(e) => {
                e.stopPropagation();
                pick(g.items[0].item);
              }}
            >
              {renderOrigin?.(g.items[0].item)}
            </button>
            {/* Its name next to the dot while pointed at. */}
            {hot === g.items[0].key && (
              <span className={`origin-label${labelSide(view, g.u, g.v)}`} aria-hidden="true">
                {renderGlyph(g.items[0].item)}
                {label(g.items[0].item)}
              </span>
            )}
          </AtPoint>
        ) : (
          <AtPoint key={`o${g.key}`} u={g.u} v={g.v} className="stack-origin is-shared">
            <button
              type="button"
              className={`origin-dot origin-stack${renderOrigin ? " has-icon" : ""}`}
              aria-expanded={sub === g.key}
              aria-label={t("radar.sameThrowSpot", { count: g.items.length, names: g.items.map((e) => label(e.item)).join(", ") })}
              title={g.items.map((e) => label(e.item)).join("\n")}
              onClick={(e) => {
                e.stopPropagation();
                setSub(sub === g.key ? null : g.key);
              }}
            >
              {renderOrigin ? (
                <>
                  {renderOrigin(g.items[0].item)}
                  <span className="origin-count">{g.items.length}</span>
                </>
              ) : (
                g.items.length
              )}
            </button>
          </AtPoint>
        ),
      )}
      {/* An open stack shows alone with its throw spots: the other markers step aside. */}
      {groups.filter((g) => !opened || !byDots || g.key === opened.key).map((g) =>
        g.items.length === 1 ? (
          <AtPoint key={g.key} u={g.u} v={g.v}>
            {renderMarker(g.items[0])}
          </AtPoint>
        ) : (
          <AtPoint key={g.key} u={g.u} v={g.v} className="stack-spot">
            <button
              type="button"
              className={`marker stack-marker${isHot && g.items.some(isHot) ? " is-hot" : ""}`}
              aria-expanded={opened?.key === g.key}
              aria-pressed={g.items.some(isSelected)}
              aria-label={t("radar.stack", { count: g.items.length, names: g.items.map(label).join(", ") })}
              title={g.items.map(label).join("\n")}
              onClick={(e) => {
                e.stopPropagation();
                if (opened?.key === g.key) close();
                else {
                  setOpen({ key: g.key, u: g.u, v: g.v });
                  setSub(null);
                }
              }}
            >
              <StackPill items={g.items} renderGlyph={renderPillGlyph ?? renderGlyph} kindOf={kindOf} sideOf={sideOf} marks={!!renderPillGlyph} />
            </button>
          </AtPoint>
        ),
      )}
      {opened &&
        (subGroup || byDots ? (
          subGroup && (
          <StackList
            at={subGroup}
            items={subGroup.items.map((e) => e.item)}
            title={t("radar.sameThrowSpotTitle", { count: subGroup.items.length })}
            onClose={() => setSub(null)}
            onHot={setHot}
            renderGlyph={renderGlyph}
            label={label}
            detail={detail}
            isSelected={isSelected}
            onSelect={pick}
          />
          )
        ) : (
          <StackList
            at={opened}
            items={opened.items}
            title={t("radar.stackTitle", { count: opened.items.length })}
            onClose={close}
            onHot={setHot}
            renderGlyph={renderGlyph}
            label={label}
            detail={detail}
            isSelected={isSelected}
            onSelect={pick}
          />
        ))}
    </>
  );
}

function StackList<T extends MapItem>({
  at: spot,
  items,
  title,
  onClose,
  onHot,
  renderGlyph,
  label,
  detail,
  isSelected,
  onSelect,
}: { at: { u: number; v: number }; items: T[]; title: string; onClose: () => void; onHot?: (key: string | null) => void } & ListProps<T>) {
  const view = useRadarView();
  const box = useRef<HTMLDivElement>(null);

  // A click anywhere else, or Escape, closes the list.
  useEffect(() => {
    const away = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!box.current?.contains(el) && !el.closest(".stack-marker, .stack-origin, .radar-zoom")) onClose();
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("click", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("click", away);
      document.removeEventListener("keydown", esc);
    };
  }, [onClose]);

  if (!view.overlay) return null;
  const at = view.toScreen(spot.u, spot.v);
  const w = Math.min(240, view.width - 16);
  const left = Math.max(8, Math.min(at.x - w / 2, view.width - w - 8));
  const below = at.y < view.width / 2;
  const style = below ? { left, top: at.y + 20, width: w } : { left, bottom: view.width - at.y + 20, width: w };
  return createPortal(
    <div ref={box} className="stack-list glass" style={style} role="dialog" aria-label={title} onPointerLeave={() => onHot?.(null)}>
      <p className="eyebrow">{title}</p>
      <ul>
        {items.map((item) => (
          <li key={item.key}>
            <button
              type="button"
              className="mini-item"
              aria-pressed={isSelected(item)}
              onPointerEnter={() => onHot?.(item.key)}
              onFocus={() => onHot?.(item.key)}
              onClick={() => onSelect(item)}
            >
              {renderGlyph(item)}
              <span>{label(item)}</span>
              {detail && <span className="muted small">{detail(item)}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>,
    view.overlay,
  );
}
